/** @module share */

import { liveEvents } from '../../../../assets/js/application/index.js';
import { networks } from './networks.js';

/**
 * Initiates Share API.
 *
 * @param {NodeList} shareElements - List of elements with share functionality.
 * @param {string} selector - Selector used for finding the elements provided by parameter <code>shareElements</code>.
 *
 * @return {Promise<void>} Resolves once everything is loaded and initialized.
 */
export async function initShare(shareElements, selector) {
    const canonicalLink = document.querySelector('link[rel=canonical]'),
        canonical = canonicalLink ? canonicalLink.href : null,
        documentTitle = document.title || '',
        documentDescription = (document.querySelector('[name~=description]') || {}).content || '',
        events = await liveEvents(),
        listenerOptions = { passive: true };

    function renderNetworks({ url, title, text }) {
        let networksHTML = networks(url, title, text).reduce((html, { active, icon, name, href }) => {
            if (active) {
                html += `<li><a class="share-content__network anchor--${icon || name.toLowerCase()} no-anchor-icon"
                    data-network="${name}"
                    target="_blank"
                    rel="noreferrer"
                    href="${href}"
                >
                    ${CONFIG.i10n.share[name] || name}
                </a></li>`;
            }

            return html;
        }, '');

        // TODO: add loading indicator + success indicator to copy button
        networksHTML += `<li><button class="anchor share-content__network anchor--link" data-share-copy-link>
                    ${CONFIG.i10n.share.copyLink}
                </button></li>`;

        if ('share' in navigator) {
            networksHTML += `<li><button class="anchor share-content__network anchor--angle-right" data-native-share-trigger>
                    ${CONFIG.i10n.share.more}
                </button></li>`;
        }

        document.querySelectorAll('[data-share-networks]').forEach((container) => {
            container.innerHTML = networksHTML;
        });
    }

    events.addEventListener(
        selector,
        'click',
        ({ target }) => {
            let documentUrl;

            if (target.dataset.img) {
                documentUrl = target.dataset.img;
            } else {
                documentUrl = canonical || location.href;
            }

            const shareDetails = {
                title: target.dataset.title || documentTitle,
                url: target.dataset.url || documentUrl,
                text: target.dataset.description || documentDescription,
            };

            function onCopyLinkClick() {
                navigator.clipboard.writeText(documentUrl);
            }

            function onNativeShareClick() {
                navigator.share(shareDetails);
            }

            events.removeEventListener('[data-share-copy-link]', 'click', onCopyLinkClick, listenerOptions);
            events.addEventListener('[data-share-copy-link]', 'click', onCopyLinkClick, listenerOptions);

            if ('share' in navigator) {
                events.removeEventListener('[data-native-share-trigger]', 'click', onNativeShareClick, listenerOptions);
                events.addEventListener('[data-native-share-trigger]', 'click', onNativeShareClick, listenerOptions);
            }

            renderNetworks(shareDetails);
        },
        listenerOptions,
    );
}
